import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, Image, Platform, useWindowDimensions, type TextStyle } from 'react-native';
import { resolveWebLayoutWidth } from '../../shared/platform/webLayout';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQueryClient } from '@tanstack/react-query';
import { SvgIcon } from '../../components/common/SvgIcon';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { MainLoader } from '../../components/load-main/MainLoader';
import { Clickable } from '../../components/common/Clickable';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { ensureAsciiDateString } from '../../shared/utils/apiParams';
import {
  clearMenuSelections,
  isPastMenuDate,
  refetchPastDateMenuAfterRating,
  removeMenuItemFromBooking,
  sendMenuRating,
} from '../../data/domains/menu';
import {
  fetchCalendarDateSelectionMenu,
  invalidateCalendarDateSelectionMenuCache,
  readCalendarDateSelectionMenuFromCache,
  type CalendarDateSelectionFoodItem,
} from '../../data/domains/calendar';
import { useAuthStore } from '../../shared/auth/store';
import { useSubscriptionContext } from '../../data/domains/subscription';
import { useNutritionUiEnabled } from '../../data/domains/config';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../../shared/theme/sharedStyles';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import {
  MEAL_SNACK_TAG_BORDER_RADIUS,
  MEAL_SNACK_TAG_LONG_HEIGHT,
  MEAL_SNACK_TAG_LONG_PADDING_H,
  MEAL_SNACK_TAG_LONG_PADDING_V,
  MEAL_SNACK_TAG_OVERLAY_BG,
  MENU_FLOW_ITEM_BADGE_INSET,
  MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
  MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP,
  MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
  MENU_FLOW_ITEM_CARD_HEIGHT,
  MENU_FLOW_ITEM_CARD_INSET,
  MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
  MENU_FLOW_ITEM_CONTENT_PADDING_H,
  MENU_FLOW_ITEM_CONTENT_PADDING_V,
  MENU_FLOW_ITEM_DESC_FONT_SIZE,
  MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
  MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
  MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
  MENU_FLOW_ITEM_IMAGE_WIDTH,
  MENU_FLOW_ITEM_MACRO_GAP,
  MENU_FLOW_ITEM_MACRO_LABEL_FONT_SIZE,
  MENU_FLOW_ITEM_MACRO_VALUE_FONT_SIZE,
  MENU_FLOW_ITEM_TITLE_FONT_SIZE,
  MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
} from '../../foodMenu/components/menuFlowItemCardShared';
import { DateSelectionMacroSummaryBar, getDateSelectionMacroScrollClearance } from './DateSelectionMacroSummaryBar';
import type { MacroTotals } from '../../data/domains/menu';
import { useTranslation, getApiText, getLocale, formatNumber, layoutLtrStyle } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import moment from 'moment';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};

/** Interactive stars sit in the same slot as selection’s read-only rating row — card height stays `MENU_FLOW_ITEM_CARD_HEIGHT`. */
const RATING_ROW_HEIGHT = 22;

type DateSelectionCardMetrics = ReturnType<typeof buildDateSelectionCardMetrics>;

function buildDateSelectionCardMetrics() {
  return {
    star_size: 14,
    star_hit: RATING_ROW_HEIGHT,
    rating_gap: 1,
    rating_action_gap: 6,
    save_btn_pad_h: 8,
    save_btn_pad_v: 3,
    save_btn_radius: 8,
    save_btn_min_h: RATING_ROW_HEIGHT,
    save_font: 11,
    delete_btn_size: 28,
    delete_btn_radius: 14,
    delete_inset: 4,
  };
}

interface FoodItem extends CalendarDateSelectionFoodItem {}

export type DateSelectionPopupMode = 'selection' | 'rating' | 'displaySelection';

function createDateSelectionPopupStyles(c: ThemeColors, m: DateSelectionCardMetrics) {
  return StyleSheet.create({
    container: {
      backgroundColor: c.backgroundElevated,
      maxHeight: '88%',
      borderTopLeftRadius: DASH_STUDIO_RADIUS,
      borderTopRightRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden' as const,
    },
    /** Clean title row — no solid strip; sheet `backgroundElevated` shows through. */
    sheetHeader: {
      backgroundColor: 'transparent',
      borderBottomWidth: 0,
    },
    content: { flex: 1 },
    contentContainer: { paddingHorizontal: appTheme.spacing.lg, paddingBottom: appTheme.spacing.sm },
    /** Transparent float host — only the tab pill below carries a fill. */
    topChrome: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 12,
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: 2,
      paddingBottom: 8,
      backgroundColor: 'transparent',
    },
    boxTabsRow: {
      ...layoutLtrStyle,
      flexDirection: 'row',
      alignItems: 'stretch',
      width: '100%',
      gap: 0,
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      backgroundColor: c.surface,
    },
    boxTab: {
      flex: 1,
      width: '50%',
      minHeight: 40,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 12,
    },
    boxTabText: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 0.2,
      ...ANDROID_TEXT_COMPACT,
    },
    loadingContainer: { alignItems: 'center', justifyContent: 'center', padding: 40 },
    loadingText: {
      marginTop: appTheme.spacing.sm,
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    emptyContainer: { alignItems: 'center', justifyContent: 'center', padding: 40 },
    emptyText: {
      marginTop: appTheme.spacing.sm,
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      textAlign: 'center',
    },
    /** Fixed-height card — matches food menu `MenuFlowItemCard` geometry. */
    mealCardOuter: {
      height: MENU_FLOW_ITEM_CARD_HEIGHT,
      marginVertical: MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL,
      overflow: 'hidden',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      borderWidth: MENU_FLOW_ITEM_CARD_BORDER_WIDTH,
      borderColor: 'transparent',
    },
    mealCardRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      flex: 1,
      padding: MENU_FLOW_ITEM_CARD_INSET,
      gap: MENU_FLOW_ITEM_CARD_INSET,
      ...horizontalLtrDirectionStyle,
    },
    imageSection: {
      width: MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH,
      alignSelf: 'center',
      justifyContent: 'center',
    },
    imageInner: {
      width: MENU_FLOW_ITEM_IMAGE_WIDTH,
      height: MENU_FLOW_ITEM_IMAGE_WIDTH,
      borderRadius: MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS,
      overflow: 'hidden',
      backgroundColor: c.borderLight,
    },
    mealImage: {
      width: '100%',
      height: '100%',
      resizeMode: 'cover',
    },
    badgeOverlay: {
      position: 'absolute',
      top: MENU_FLOW_ITEM_BADGE_INSET,
      left: MENU_FLOW_ITEM_BADGE_INSET,
      right: MENU_FLOW_ITEM_BADGE_INSET,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
    },
    typeTag: {
      backgroundColor: MEAL_SNACK_TAG_OVERLAY_BG,
      height: MEAL_SNACK_TAG_LONG_HEIGHT,
      paddingHorizontal: MEAL_SNACK_TAG_LONG_PADDING_H,
      paddingVertical: MEAL_SNACK_TAG_LONG_PADDING_V,
      borderRadius: MEAL_SNACK_TAG_BORDER_RADIUS,
      justifyContent: 'center',
      alignItems: 'center',
    },
    typeTagText: {
      color: c.onPrimary,
      fontSize: 9,
      fontWeight: '800',
      textTransform: 'uppercase',
      ...ANDROID_TEXT_COMPACT,
    },
    contentSection: {
      flex: 1,
      minHeight: 0,
      justifyContent: 'space-between',
      minWidth: 0,
      overflow: 'hidden',
    },
    textArea: {
      flex: 1,
      minHeight: 0,
      justifyContent: 'flex-start',
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      minHeight: MENU_FLOW_ITEM_TITLE_MIN_HEIGHT,
      paddingTop: MENU_FLOW_ITEM_CONTENT_PADDING_V,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      ...horizontalLtrDirectionStyle,
    },
    titleRowWithDelete: {
      paddingRight: m.delete_btn_size + m.delete_inset,
    },
    itemTitle: {
      flex: 1,
      minWidth: 0,
      width: '100%',
      fontSize: MENU_FLOW_ITEM_TITLE_FONT_SIZE,
      fontWeight: '600',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    itemDescription: {
      width: '100%',
      alignSelf: 'stretch',
      fontSize: MENU_FLOW_ITEM_DESC_FONT_SIZE,
      color: c.textSecondary,
      marginTop: MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      ...ANDROID_TEXT_COMPACT,
    },
    comment: {
      width: '100%',
      alignSelf: 'stretch',
      fontSize: 10,
      lineHeight: 13,
      color: c.textSecondary,
      fontStyle: 'italic',
      marginTop: 4,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      ...ANDROID_TEXT_COMPACT,
    },
    ratingDisplayRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: m.rating_gap,
      height: m.star_hit,
      marginTop: 2,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      flexShrink: 0,
    },
    ratingActionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: m.rating_action_gap,
      height: m.star_hit,
      marginTop: 2,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      flexShrink: 0,
    },
    ratingStarsWrap: {
      flex: 1,
      minWidth: 0,
      justifyContent: 'center',
    },
    ratingSaveBtn: {
      paddingHorizontal: m.save_btn_pad_h,
      paddingVertical: m.save_btn_pad_v,
      borderRadius: m.save_btn_radius,
      backgroundColor: c.primary,
      height: m.save_btn_min_h,
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
    },
    ratingSaveBtnText: {
      fontSize: m.save_font,
      fontWeight: '700',
      color: c.onPrimary ?? c.white,
    },
    ratingSaveBtnDisabled: {
      opacity: 0.55,
    },
    macroRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: MENU_FLOW_ITEM_MACRO_GAP,
      paddingTop: MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP,
      paddingHorizontal: MENU_FLOW_ITEM_CONTENT_PADDING_H,
      minHeight: MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT,
      flexShrink: 0,
      ...horizontalLtrDirectionStyle,
    },
    macroPoint: { alignItems: 'flex-start', position: 'relative' as const },
    macroDot: { width: 12, height: 2, borderRadius: 1, marginBottom: 2 },
    macroValue: {
      fontSize: MENU_FLOW_ITEM_MACRO_VALUE_FONT_SIZE,
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT_COMPACT,
    },
    macroLabel: {
      fontSize: MENU_FLOW_ITEM_MACRO_LABEL_FONT_SIZE,
      color: c.textLight,
      ...ANDROID_TEXT_COMPACT,
    },
    deleteItemBtnWrap: {
      position: 'absolute',
      right: m.delete_inset,
      top: m.delete_inset,
      width: m.delete_btn_size,
      height: m.delete_btn_size,
      borderRadius: m.delete_btn_radius,
      zIndex: 2,
    },
    deleteItemBtn: {
      width: m.delete_btn_size,
      height: m.delete_btn_size,
      borderRadius: m.delete_btn_radius,
      backgroundColor: (hexToRgba(c.danger, 0.14) as string),
      justifyContent: 'center',
      alignItems: 'center',
    },
    footer: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.sm,
      gap: appTheme.spacing.sm,
      backgroundColor: 'transparent',
    },
    sheetBody: {
      flex: 1,
      minHeight: 0,
    },
    /** Transparent host — macro bar shell (and CTAs) carry their own fills. */
    bottomOverlay: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 10,
    },
    footerRow: {
      flexDirection: 'row',
      gap: appTheme.spacing.sm,
      alignItems: 'stretch',
    },
    footerGrow: { flex: 1, minWidth: 0 },
    buttonDisabled: { opacity: 0.55 },
  });
}

interface DateSelectionPopupProps {
  isVisible: boolean;
  date: string;
  mode: DateSelectionPopupMode;
  /** When true, show Box 1 / Box 2 tabs and load menu per box. */
  isDoubleBox?: boolean;
  onClose: (refreshNeeded?: boolean) => void;
  /** Parent closes the sheet, then runs menu navigation after `onDismissComplete` (avoids navigating while the Modal is still mounted). */
  onNavigateToMenu?: (date: string) => void;
  onClearAll?: () => void;
  /** See `BottomSheetPopup` — after slide-down finishes. */
  onDismissComplete?: () => void;
  /** Theme backdrop token for the modal dimmer (matches calendar / app chrome). */
  backdropColor?: string;
  customerId?: number;
  /** Preferred subscription id for rating API; merged context id used when missing. */
  customerSubscriptionId?: number;
}

function StarRating({
  rating,
  onRatingChange,
  star_size,
  star_hit,
  rateStarsLabel,
  colors,
}: {
  rating: number;
  onRatingChange: (r: number) => void | Promise<void>;
  star_size: number;
  star_hit: number;
  rateStarsLabel: (count: number) => string;
  colors: ThemeColors;
}) {
  const filled = colors.warning ?? '#E8A317';
  const empty = colors.borderLight ?? colors.border;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      {[1, 2, 3, 4, 5].map(star => (
        <Clickable
          key={star}
          onPress={() => onRatingChange(star)}
          style={{ width: star_hit, height: star_hit, justifyContent: 'center', alignItems: 'center' }}
          hitSlop={{ top: 6, bottom: 6, left: 1, right: 1 }}
          accessibilityLabel={rateStarsLabel(star)}
          accessibilityRole="button"
          accessibilityState={{ selected: star <= rating }}>
          <Text
            style={[
              {
                fontSize: star_size,
                lineHeight: star_hit,
                color: star <= rating ? filled : empty,
                opacity: star <= rating ? 1 : 0.45,
              },
              Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' as const } : null,
            ]}>
            ★
          </Text>
        </Clickable>
      ))}
    </View>
  );
}

function MacroPoint({ value, label, dotColor, valueStyle, labelStyle, dotStyle, containerStyle }: { value: number; label: string; dotColor: string; valueStyle: object; labelStyle: object; dotStyle: object; containerStyle: object }) {
  return (
    <View style={containerStyle}>
      <View style={[dotStyle, { backgroundColor: dotColor }]} />
      <Text style={valueStyle}>
        {formatNumber(value)}
        <Text style={labelStyle}>{label}</Text>
      </Text>
    </View>
  );
}

function computeMacroTotalsFromFoodItems(items: FoodItem[]): MacroTotals {
  const acc = { calories_cents: 0, protein_cents: 0, carbs_cents: 0, fats_cents: 0 };
  for (const item of items) {
    const quantity = item.quantity ?? 1;
    if (quantity <= 0) {
      continue;
    }
    acc.calories_cents += Math.round(100 * item.calories * quantity);
    acc.protein_cents += Math.round(100 * item.protein * quantity);
    acc.carbs_cents += Math.round(100 * item.carbs * quantity);
    acc.fats_cents += Math.round(100 * item.fats * quantity);
  }
  return {
    calories: Math.round(acc.calories_cents / 100),
    protein: Math.round(acc.protein_cents / 100),
    carbs: Math.round(acc.carbs_cents / 100),
    fats: Math.round(acc.fats_cents / 100),
  };
}

const DateSelectionPopup: React.FC<DateSelectionPopupProps> = ({
  isVisible,
  date,
  mode,
  isDoubleBox = false,
  onClose,
  onNavigateToMenu,
  onClearAll,
  onDismissComplete,
  backdropColor,
  customerId: customerIdProp,
  customerSubscriptionId: customerSubscriptionIdProp,
}) => {
  const user = useAuthStore(s => s.user);
  const { t } = useTranslation();
  const rtl = useRtl();
  const nutrition_ui_enabled = useNutritionUiEnabled();
  const insets = useSafeAreaInsets();
  const { width: window_width, fontScale: font_scale } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const customer_id = customerIdProp ?? user?.id ?? 0;
  const query_client = useQueryClient();
  const { activeSubscriptions } = useSubscriptionContext();
  const [active_box, set_active_box] = useState<'box1' | 'box2'>('box1');
  const is_box2 = isDoubleBox && active_box === 'box2';

  const colors = useThemeColors() ?? appTheme.colors;
  const card_metrics = useMemo(() => buildDateSelectionCardMetrics(), []);
  const styles = useMemo(() => createDateSelectionPopupStyles(colors, card_metrics), [colors, card_metrics]);

  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(colors),
      label: createStackedFooterPrimaryLabelStyle(colors),
    }),
    [colors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(colors),
      label: createStackedFooterSecondaryLabelStyle(colors),
    }),
    [colors],
  );

  const effective_subscription_id = useMemo(() => {
    const from_prop = Number(customerSubscriptionIdProp);
    if (Number.isFinite(from_prop) && from_prop > 0) {
      return from_prop;
    }
    const from_ctx = activeSubscriptions[0]?.id;
    return typeof from_ctx === 'number' && from_ctx > 0 ? from_ctx : 0;
  }, [customerSubscriptionIdProp, activeSubscriptions]);

  const [selectedFood, setSelectedFood] = useState<FoodItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<number | null>(null);
  const [draft_ratings, setDraftRatings] = useState<Record<number, number>>({});
  const [saving_rating_item_id, setSavingRatingItemId] = useState<number | null>(null);

  useEffect(() => {
    if (!isVisible) {
      setSelectedFood([]);
      setIsLoading(false);
      setIsClearing(false);
      setDeletingItemId(null);
      setDraftRatings({});
      setSavingRatingItemId(null);
      set_active_box('box1');
      return;
    }
    if (!date || !customer_id) {
      return;
    }

    const cached_items = readCalendarDateSelectionMenuFromCache(query_client, customer_id, date, is_box2);
    if (cached_items != null) {
      setSelectedFood(cached_items);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }

    let cancelled = false;
    void fetchCalendarDateSelectionMenu(query_client, customer_id, date, is_box2)
      .then(items => {
        if (!cancelled) {
          setSelectedFood(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelectedFood([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isVisible, date, customer_id, query_client, is_box2]);

  useEffect(() => {
    if (isVisible) {
      set_active_box('box1');
      setDraftRatings({});
    }
  }, [isVisible, date, isDoubleBox]);

  const locale = getLocale();
  const formattedDate = date
    ? moment(ensureAsciiDateString(date) || date)
        .locale(locale === 'ar' ? 'ar' : 'en')
        .format('DD MMMM YYYY')
    : '';

  const macro_totals = useMemo(() => computeMacroTotalsFromFoodItems(selectedFood), [selectedFood]);
  const show_macro_summary = nutrition_ui_enabled && !isLoading && selectedFood.length > 0;

  const handleDeleteItem = async (item: FoodItem) => {
    if (!customer_id || !date) {
      return;
    }
    setDeletingItemId(item.id);
    try {
      await removeMenuItemFromBooking({
        customer_id,
        date,
        menu_item_id: item.id,
        menu_builder_id: item.menu_builder_id ?? 0,
        customer_subscription_id: effective_subscription_id > 0 ? effective_subscription_id : undefined,
        is_double_box: is_box2,
      });
      setSelectedFood(prev => prev.filter(f => f.id !== item.id));
      invalidateCalendarDateSelectionMenuCache(query_client, customer_id, date);
      if (onClearAll && selectedFood.length === 1) {
        onClearAll();
      }
    } catch {
      // ignore
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleClearAll = async () => {
    if (!customer_id || !date || selectedFood.length === 0) {
      return;
    }
    setIsClearing(true);
    try {
      await clearMenuSelections({
        customer_id,
        date,
        is_double_box: is_box2,
      });
      setSelectedFood([]);
      invalidateCalendarDateSelectionMenuCache(query_client, customer_id, date);
      onClearAll?.();
    } catch {
      // ignore
    } finally {
      setIsClearing(false);
    }
  };

  const handleDraftRating = useCallback((item: FoodItem, new_rating: number) => {
    setDraftRatings(prev => ({ ...prev, [item.id]: new_rating }));
  }, []);

  const getDisplayRating = useCallback(
    (item: FoodItem) => {
      const draft = draft_ratings[item.id];
      return draft != null ? draft : item.my_rating;
    },
    [draft_ratings],
  );

  const hasUnsavedRating = useCallback(
    (item: FoodItem) => {
      const draft = draft_ratings[item.id];
      return draft != null && draft !== item.my_rating;
    },
    [draft_ratings],
  );

  const handleSaveRating = useCallback(
    async (item: FoodItem) => {
      const new_rating = draft_ratings[item.id];
      if (new_rating == null || new_rating === item.my_rating) {
        return;
      }
      if (!customer_id || !date) {
        return;
      }
      const sub_id = effective_subscription_id;
      if (sub_id <= 0) {
        return;
      }
      setSavingRatingItemId(item.id);
      try {
        await sendMenuRating({
          rate: new_rating,
          menu_item_id: item.id,
          date,
          customer_id,
          customer_subscription_id: sub_id,
          comment: item.comment || '',
        });
        setSelectedFood(prev => prev.map(f => (f.id === item.id ? { ...f, my_rating: new_rating } : f)));
        setDraftRatings(prev => {
          const next = { ...prev };
          delete next[item.id];
          return next;
        });
        if (isPastMenuDate(date)) {
          await refetchPastDateMenuAfterRating(query_client, customer_id, date);
          const refreshed = await fetchCalendarDateSelectionMenu(query_client, customer_id, date, is_box2);
          setSelectedFood(refreshed);
        }
      } catch {
        // keep draft so user can retry
      } finally {
        setSavingRatingItemId(null);
      }
    },
    [customer_id, date, draft_ratings, effective_subscription_id, query_client, is_box2],
  );

  const renderFoodItem = (item: FoodItem) => {
    const is_meal = item.food_type_category === 'meal';
    const title = getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name;
    const desc = getApiText(item as unknown as Record<string, unknown>, 'description', 'a_description') || item.description;

    const display_rating = getDisplayRating(item);
    const show_save_rating = mode === 'rating' && hasUnsavedRating(item);
    const is_saving_rating = saving_rating_item_id === item.id;

    return (
      <View key={`${item.id}-${item.menu_builder_id ?? 0}`} style={styles.mealCardOuter}>
        <View style={styles.mealCardRow}>
          <View style={styles.imageSection}>
            <View style={styles.imageInner}>
              <Image source={{ uri: item.attachment || 'https://via.placeholder.com/150' }} style={styles.mealImage} />
              <View style={styles.badgeOverlay} pointerEvents="none">
                <View style={styles.typeTag}>
                  <Text style={styles.typeTagText} numberOfLines={1}>
                    {is_meal ? t('menu.meal') : t('menu.snack')}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <View style={styles.contentSection}>
            <View style={styles.textArea}>
              <View style={[styles.titleRow, mode === 'selection' && styles.titleRowWithDelete]}>
                <Text style={[styles.itemTitle, rtl.leadingMediaCopyTextStyle]} numberOfLines={1}>
                  {title}
                </Text>
              </View>
              {desc ? (
                <Text style={[styles.itemDescription, rtl.leadingMediaCopyTextStyle]} numberOfLines={2}>
                  {desc}
                </Text>
              ) : null}
              {item.comment && mode !== 'rating' ? (
                <Text style={[styles.comment, rtl.leadingMediaCopyTextStyle]} numberOfLines={1}>
                  {item.comment}
                </Text>
              ) : null}
              {mode === 'rating' ? (
                <View style={styles.ratingActionRow}>
                  <View style={styles.ratingStarsWrap}>
                    <StarRating
                      rating={display_rating}
                      onRatingChange={r => handleDraftRating(item, r)}
                      star_size={card_metrics.star_size}
                      star_hit={card_metrics.star_hit}
                      rateStarsLabel={count => t('dateSelection.rateStars').replace('{{count}}', String(count))}
                      colors={colors}
                    />
                  </View>
                  {show_save_rating ? (
                    <Clickable
                      onPress={() => handleSaveRating(item)}
                      disabled={is_saving_rating}
                      accessibilityLabel={t('common.save')}
                      accessibilityRole="button">
                      <View style={[styles.ratingSaveBtn, is_saving_rating && styles.ratingSaveBtnDisabled]}>
                        {is_saving_rating ? (
                          <ButtonLoader size={14} color={colors.onPrimary ?? colors.white} />
                        ) : (
                          <Text style={styles.ratingSaveBtnText}>{t('common.save')}</Text>
                        )}
                      </View>
                    </Clickable>
                  ) : null}
                </View>
              ) : (
                item.my_rating > 0 && (
                  <View style={styles.ratingDisplayRow}>
                    {[1, 2, 3, 4, 5].map(star => (
                      <Text
                        key={star}
                        style={{
                          fontSize: card_metrics.star_size,
                          lineHeight: card_metrics.star_hit,
                          color: star <= item.my_rating ? (colors.warning ?? '#E8A317') : colors.borderLight,
                          opacity: star <= item.my_rating ? 1 : 0.35,
                          ...(Platform.OS === 'android' ? { includeFontPadding: false, textAlignVertical: 'center' as const } : {}),
                        }}>
                        ★
                      </Text>
                    ))}
                  </View>
                )
              )}
            </View>
            {nutrition_ui_enabled ? (
              <View style={styles.macroRow}>
                <MacroPoint
                  value={Math.round(item.calories)}
                  label=" Kcal"
                  dotColor={colors.calorie}
                  valueStyle={styles.macroValue}
                  labelStyle={styles.macroLabel}
                  dotStyle={styles.macroDot}
                  containerStyle={styles.macroPoint}
                />
                <MacroPoint
                  value={Math.round(item.protein)}
                  label={` ${t('menu.proteinShort')}`}
                  dotColor={colors.protein}
                  valueStyle={styles.macroValue}
                  labelStyle={styles.macroLabel}
                  dotStyle={styles.macroDot}
                  containerStyle={styles.macroPoint}
                />
                <MacroPoint
                  value={Math.round(item.carbs)}
                  label={` ${t('menu.carbsShort')}`}
                  dotColor={colors.carbs}
                  valueStyle={styles.macroValue}
                  labelStyle={styles.macroLabel}
                  dotStyle={styles.macroDot}
                  containerStyle={styles.macroPoint}
                />
                <MacroPoint
                  value={Math.round(item.fats)}
                  label={` ${t('menu.fatShort')}`}
                  dotColor={colors.fats}
                  valueStyle={styles.macroValue}
                  labelStyle={styles.macroLabel}
                  dotStyle={styles.macroDot}
                  containerStyle={styles.macroPoint}
                />
              </View>
            ) : null}
          </View>
        </View>
        {mode === 'selection' && (
          <Clickable onPress={() => handleDeleteItem(item)} disabled={deletingItemId === item.id} accessibilityLabel={t('dateSelection.removeItem')} accessibilityRole="button">
            <View style={styles.deleteItemBtnWrap}>
              <View style={styles.deleteItemBtn}>{deletingItemId === item.id ? <ButtonLoader size={14} color={colors.danger} /> : <SvgIcon name="trash" size={16} color={colors.danger} />}</View>
            </View>
          </Clickable>
        )}
      </View>
    );
  };

  const handleClose = useCallback(
    (refreshNeeded?: boolean) => {
      onClose(refreshNeeded);
    },
    [onClose],
  );

  const footer_block_height = appTheme.spacing.sm * 2 + 52;
  const scroll_bottom_clearance = useMemo(
    () => getDateSelectionMacroScrollClearance(layout_width, font_scale, show_macro_summary, footer_block_height),
    [layout_width, font_scale, show_macro_summary, footer_block_height],
  );

  const navigate_date = date ? ensureAsciiDateString(date) || date : '';
  const box_tabs_clearance = isDoubleBox ? 54 : 0;
  const box_tabs_width = (layout_width - appTheme.spacing.lg * 2) / 2;
  return (
    <BottomSheetPopup
      visible={isVisible}
      onClose={() => handleClose()}
      onRequestClose={() => handleClose()}
      onDismissComplete={onDismissComplete}
      backdropColor={backdropColor ?? colors.backdrop}
      title={formattedDate}
      animationType="slide"
      showDragHandle
      sheetTopBorderRadius={DASH_STUDIO_RADIUS}
      frostedSheetHeader
      headerStyle={styles.sheetHeader}
      sheetStyle={styles.container}>
      <View style={styles.sheetBody}>
        <ScrollView
          style={styles.content}
          contentContainerStyle={[
            styles.contentContainer,
            { paddingTop: box_tabs_clearance, paddingBottom: scroll_bottom_clearance },
          ]}
          keyboardShouldPersistTaps="handled">
          {isLoading ? (
            <View style={styles.loadingContainer}>
              <MainLoader size={48} color={colors.primary} />
              <Text style={styles.loadingText}>{mode === 'rating' ? t('common.loading') : t('dateSelection.loadingSelections')}</Text>
            </View>
          ) : selectedFood.length > 0 ? (
            selectedFood.map(renderFoodItem)
          ) : (
            <View style={styles.emptyContainer}>
              <SvgIcon name="alert" size={48} color={colors.textSecondary} />
              <Text style={styles.emptyText}>{t('dateSelection.noFoodOrdersForDate')}</Text>
            </View>
          )}
        </ScrollView>

        {isDoubleBox ? (
          <View style={styles.topChrome} pointerEvents="box-none">
            <View style={styles.boxTabsRow}>
              {(['box1', 'box2'] as const).map(box => {
                const active = active_box === box;
                return (
                  <Clickable
                    key={box}
                    onPress={() => {
                      set_active_box(box);
                      setDraftRatings({});
                    }}
                    accessibilityLabel={box === 'box1' ? t('menu.box1') : t('menu.box2')}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    style={{ flex: 1 }}
                    contentStyle={{ flex: 0.5, width: box_tabs_width, alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                    <View
                      style={[
                        styles.boxTab,
                        {
                          backgroundColor: active ? colors.primary : 'transparent',
                        },
                      ]}>
                      <Text
                        style={[
                          styles.boxTabText,
                          { color: active ? colors.onPrimary ?? colors.white : colors.textSecondary },
                        ]}>
                        {box === 'box1' ? t('menu.box1') : t('menu.box2')}
                      </Text>
                    </View>
                  </Clickable>
                );
              })}
            </View>
          </View>
        ) : null}

        <View style={styles.bottomOverlay} pointerEvents="box-none">
          {show_macro_summary ? <DateSelectionMacroSummaryBar macroTotals={macro_totals} /> : null}

          <View style={styles.footer}>
            {mode === 'selection' ? (
          <View style={styles.footerRow}>
            {selectedFood.length > 0 ? (
              <>
                <View style={styles.footerGrow}>
                  <Clickable
                    onPress={handleClearAll}
                    disabled={isClearing}
                    accessibilityLabel={t('dateSelection.clearAll')}
                    accessibilityRole="button"
                    style={{ width: '100%' }}
                    contentStyle={{ alignSelf: 'stretch' }}>
                    <View style={[secondary_cta.surface, isClearing && styles.buttonDisabled, { justifyContent: 'center', minHeight: 52 }]}>
                      {isClearing ? <ButtonLoader size={18} color={colors.primary} /> : <Text style={secondary_cta.label}>{t('dateSelection.clearAll')}</Text>}
                    </View>
                  </Clickable>
                </View>
                {onNavigateToMenu ? (
                  <View style={styles.footerGrow}>
                    <Clickable
                      onPress={() => (navigate_date ? onNavigateToMenu(navigate_date) : undefined)}
                      accessibilityLabel={t('dateSelection.goToMenu')}
                      accessibilityRole="button"
                      style={{ width: '100%' }}
                      contentStyle={{ alignSelf: 'stretch' }}>
                      <View style={[primary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
                        <Text style={primary_cta.label}>{t('dateSelection.goToMenu')}</Text>
                      </View>
                    </Clickable>
                  </View>
                ) : null}
              </>
            ) : onNavigateToMenu ? (
              <>
                <View style={styles.footerGrow}>
                  <Clickable
                    onPress={() => handleClose()}
                    accessibilityLabel={t('common.close')}
                    accessibilityRole="button"
                    style={{ width: '100%' }}
                    contentStyle={{ alignSelf: 'stretch' }}>
                    <View style={[secondary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
                      <Text style={secondary_cta.label}>{t('common.close')}</Text>
                    </View>
                  </Clickable>
                </View>
                <View style={styles.footerGrow}>
                  <Clickable
                    onPress={() => (navigate_date ? onNavigateToMenu(navigate_date) : undefined)}
                    accessibilityLabel={t('dateSelection.goToMenu')}
                    accessibilityRole="button"
                    style={{ width: '100%' }}
                    contentStyle={{ alignSelf: 'stretch' }}>
                    <View style={[primary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
                      <Text style={primary_cta.label}>{t('dateSelection.goToMenu')}</Text>
                    </View>
                  </Clickable>
                </View>
              </>
            ) : (
              <View style={styles.footerGrow}>
                <Clickable
                  onPress={() => handleClose()}
                  accessibilityLabel={t('common.close')}
                  accessibilityRole="button"
                  style={{ width: '100%' }}
                  contentStyle={{ alignSelf: 'stretch' }}>
                  <View style={[secondary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
                    <Text style={secondary_cta.label}>{t('common.close')}</Text>
                  </View>
                </Clickable>
              </View>
            )}
          </View>
        ) : (
          <Clickable
            onPress={() => handleClose(true)}
            accessibilityLabel={t('common.done')}
            accessibilityRole="button"
            style={{ width: '100%' }}
            contentStyle={{ alignSelf: 'stretch' }}>
            <View style={[primary_cta.surface, { justifyContent: 'center', minHeight: 52 }]}>
              <Text style={primary_cta.label}>{t('common.done')}</Text>
            </View>
          </Clickable>
        )}
          </View>
        </View>
      </View>
    </BottomSheetPopup>
  );
};

export default DateSelectionPopup;
